import { Card } from '@acceso/design-system';
import clsx from 'clsx';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { GeneralHeader } from '@/components/HeaderPage/HeaderPage';
import {
  FrameAction,
  FrameActionType,
} from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/types';
import { BroadcastScheduleProgramBodySegmentToolkitNavigation } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitNavigation';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useDeleteClipPeople } from '@/features/clips/api/delete-clips-people';
import { usePatchClipPeople } from '@/features/clips/api/patch-clips-people';
import { useClipFeaturedPeoplePermissions } from '@/features/clips/hooks/useClipFeaturedPeoplePermissions';
import { TClipPeople, TPatchClipPeople, TPostClipPeople } from '@/features/clips/types';
import { usePresencePreviewBox } from '@/hooks/use-presence-preview-box';

import { ClipToolkitPeopleForm } from './components/ClipToolkitPeopleForm';
import { ClipToolkitPeopleTable } from './components/ClipToolkitPeopleTable';

export default function ClipToolkitPeople() {
  const {
    formBox,
    personaClipData,
    activeClip,
    setVisibleBox,
    setFormBox,
    setPersonaClipData,
    setActiveClip,
    handleTooltikClick,
    setClipItems,
    videoControlRef,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: deleteClipPeople } = useDeleteClipPeople();
  const { mutateAsync: patchClipPeople } = usePatchClipPeople();
  const { t } = useTranslation();

  const { canCreate, canUpdate, canDelete } = useClipFeaturedPeoplePermissions();

  const personaClipDataIdRef = useRef(personaClipData?.id);
  personaClipDataIdRef.current = personaClipData?.id;

  const previewPresenceBox = usePresencePreviewBox({
    referenceStartDatetime: activeClip?.start_datetime,
    setVisibleBox,
    videoControlRef,
  });

  const handlePreviewBox = useCallback(
    (box: TClipPeople, frame: FrameActionType = FrameAction.START, currentFrame?: number) => {
      if (personaClipDataIdRef.current !== box.id) {
        setPersonaClipData(box);
      }

      previewPresenceBox(box, frame, currentFrame);
    },
    [previewPresenceBox, setPersonaClipData]
  );

  const handleEdit = useCallback(
    (item: TClipPeople) => {
      if (!canUpdate) return;
      setFormBox('people');
      setPersonaClipData(item);
    },
    [setFormBox, canUpdate]
  );

  const handleCreate = useCallback(() => {
    if (!canCreate) return;
    setFormBox('people');
    setPersonaClipData(null);
  }, [setFormBox, setPersonaClipData, canCreate]);

  const handleDelete = useCallback(
    (item: TClipPeople, silently?: boolean) => {
      if (!canDelete) return;
      deleteClipPeople({ id: item.id || '', silently }).then(() => {
        setActiveClip(prev => {
          if (prev) {
            return {
              ...prev,
              clip_peoples: prev.clip_peoples?.filter(people => people.id !== item.id),
            };
          }
          return prev;
        });
        setClipItems(prev =>
          prev.map(clip => {
            if (clip.id === item.clip_id) {
              return {
                ...clip,
                clip_peoples: clip.clip_peoples?.filter(people => people.id !== item.id),
              };
            }
            return clip;
          })
        );
      });
    },
    [canDelete]
  );

  const handleEditPeopleInline = useCallback(
    (item: TPostClipPeople, silently?: boolean, currentFrame?: number) => {
      void silently;
      if (!canUpdate) return;

      const patchData: TPatchClipPeople = {
        id: item.id || '',
        people_id: item.people_id,
        entity_score: item.entity_score,
        start_datetime: item.start_datetime,
        end_datetime: item.end_datetime,
        parameters: item.parameters,
        review_state: item.review_state,
      };

      patchClipPeople(patchData).then(updatedPeople => {
        setActiveClip(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            clip_peoples: prev.clip_peoples?.map(p =>
              p.id === updatedPeople.id ? updatedPeople : p
            ),
          };
        });
        setClipItems(prev => {
          if (!prev) return prev;
          return prev.map(clip => {
            if (clip.id === activeClip?.id) {
              return {
                ...clip,
                clip_peoples: clip.clip_peoples?.map(p =>
                  p.id === updatedPeople.id ? updatedPeople : p
                ),
              };
            }
            return clip;
          });
        });
        handlePreviewBox(updatedPeople, FrameAction.CURRENT, currentFrame);
      });
    },
    [canUpdate, patchClipPeople, setActiveClip, setClipItems, activeClip, handlePreviewBox]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLocaleLowerCase() === 'i') {
        event.preventDefault();
        handleCreate();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleCreate]);

  return (
    <Card
      classes={{
        rootSection: clsx('w-full flex-auto order-3 lg:order-2'),
        container: 'py-size-4',
      }}
    >
      <div className="flex w-full flex-col gap-2">
        {formBox !== 'people' ? (
          <>
            <GeneralHeader
              name={t('Persona de interés')}
              buttonName={canCreate ? t('Añadir') : undefined}
              onClick={handleCreate}
              onClose={() => handleTooltikClick(ESegmentToolkit.PERSONAS)}
            />
            <div className="flex max-h-[calc(100vh-236px)] min-h-[400px] flex-col overflow-y-auto">
              <ClipToolkitPeopleTable
                items={activeClip?.clip_peoples || []}
                onPreview={handlePreviewBox}
                onUpdate={canUpdate ? handleEdit : undefined}
                onUpdateInline={canUpdate ? handleEditPeopleInline : undefined}
                onDelete={canDelete ? handleDelete : undefined}
              />
            </div>
          </>
        ) : (
          <div className="flex flex-col gap-2">
            <ClipToolkitPeopleForm />
            <BroadcastScheduleProgramBodySegmentToolkitNavigation
              items={activeClip?.clip_peoples || []}
              activeItem={personaClipData}
              onChangeData={setPersonaClipData}
            />
          </div>
        )}
      </div>
    </Card>
  );
}
